import React, { useEffect, useState } from "react";
import { Check, Crown, Sparkles, Building2, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { useCredits } from "@/lib/CreditsContext";
import { useAuth } from "@/lib/AuthContext";
import AuthDialog from "@/components/AuthDialog";
import { toast } from "sonner";

const META = {
  starter: {
    icon: Sparkles,
    accent: "border-slate-700 text-slate-300",
    highlight: false,
    features: [
      "10 AI generations",
      "Just $1 per generation",
      "PDF + Markdown export",
      "Share with stakeholders",
      "Email support",
    ],
  },
  growth: {
    icon: Crown,
    accent: "border-cyan-500/70 text-cyan-300",
    highlight: true,
    features: [
      "15 AI generations",
      "All 6 modules — full access",
      "Priority generation queue",
      "Past documents library",
    ],
  },
  pro: {
    icon: Building2,
    accent: "border-amber-500/60 text-amber-300",
    highlight: false,
    features: [
      "40 AI generations",
      "Bulk export",
      "Multi-project switcher",
      "Premium support",
    ],
  },
};

const RAZORPAY_SDK_URL = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpaySdk() {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("SSR"));
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RAZORPAY_SDK_URL}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => reject(new Error("SDK load failed")));
      return;
    }
    const s = document.createElement("script");
    s.src = RAZORPAY_SDK_URL;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => reject(new Error("Razorpay SDK failed to load"));
    document.body.appendChild(s);
  });
}

export default function PricingDialog({ open, onOpenChange }) {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(null);
  const [pendingPack, setPendingPack] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const { refresh } = useCredits();
  const { user } = useAuth();

  useEffect(() => {
    if (!open) return;
    api.get("/payments/packs").then((r) => setPacks(r.data)).catch(() => {});
    loadRazorpaySdk().catch(() => {});
  }, [open]);

  const startCheckout = async (packId) => {
    setLoading(packId);
    try {
      await loadRazorpaySdk();
      const { data: order } = await api.post("/payments/order", {
        pack_id: packId,
      });

      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "3C AI",
        description: `${order.name} · ${order.credits} credits`,
        order_id: order.order_id,
        theme: { color: "#06B6D4" },
        prefill: user
          ? { email: user.email, name: user.name || "" }
          : undefined,
        modal: {
          ondismiss: () => {
            setLoading(null);
            toast.info("Checkout cancelled. No charge was made.");
          },
        },
        handler: async (rp) => {
          try {
            const { data: verified } = await api.post("/payments/verify", {
              razorpay_order_id: rp.razorpay_order_id,
              razorpay_payment_id: rp.razorpay_payment_id,
              razorpay_signature: rp.razorpay_signature,
            });
            refresh();
            onOpenChange?.(false);
            window.location.href = `/payment/success?order_id=${encodeURIComponent(
              verified.order_id
            )}`;
          } catch (err) {
            toast.error(
              err?.response?.data?.detail ||
                "Payment received but verification failed. Please contact support."
            );
            setLoading(null);
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(
          resp?.error?.description || "Payment failed. No credits were charged."
        );
        setLoading(null);
      });
      rzp.open();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
          e?.message ||
          "Could not start checkout. Please try again."
      );
      setLoading(null);
    }
  };

  const buy = (packId) => {
    if (!user) {
      setPendingPack(packId);
      setAuthOpen(true);
      return;
    }
    startCheckout(packId);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-950 border-slate-800 text-slate-100 max-w-4xl rounded-sm"
        data-testid="pricing-dialog"
      >
        <DialogHeader>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400 mb-1 flex items-center gap-2">
            <span className="inline-block h-1 w-6 bg-cyan-500" />
            Pricing
          </div>
          <DialogTitle className="font-display text-2xl">
            Pay only for what you generate.
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-400">
            Each AI generation consumes 1 credit · credits never expire · start
            with 2 free, no credit card.
          </DialogDescription>
        </DialogHeader>

        <div
          data-testid="starter-promo-banner"
          className="mt-3 flex items-center gap-2 rounded-sm border border-cyan-500/30 bg-cyan-500/10 px-3 py-2"
        >
          <Sparkles className="h-3.5 w-3.5 shrink-0 text-cyan-400" />
          <p className="text-xs text-cyan-200">
            <span className="font-semibold text-cyan-300">Start with Starter:</span>{" "}
            10 AI generations for $10 — just $1 per generation. No subscription.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
          {packs.map((p) => {
            const m = META[p.id] || {};
            const Icon = m.icon || Sparkles;
            return (
              <div
                key={p.id}
                data-testid={`tier-${p.id}`}
                className={`relative bg-slate-900/60 border rounded-sm p-5 flex flex-col ${
                  m.highlight
                    ? "border-cyan-500/70 shadow-[0_0_24px_-6px_rgba(6,182,212,0.5)]"
                    : "border-slate-800"
                }`}
              >
                {m.highlight && (
                  <Badge className="absolute -top-2.5 right-4 rounded-sm bg-cyan-500 text-slate-950 border-0">
                    Most popular
                  </Badge>
                )}
                <div className="flex items-center gap-2 mb-3">
                  <div className={`h-8 w-8 grid place-items-center bg-slate-950 border rounded-sm ${m.accent}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <span className="font-display text-lg font-semibold">{p.name}</span>
                </div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="font-display text-4xl font-bold text-white tracking-tight">
                    ₹{p.amount}
                  </span>
                </div>
                <div className="text-xs font-mono text-slate-500 mb-4">
                  {p.credits} credits · one-time
                </div>
                <ul className="space-y-2 text-sm text-slate-300 flex-1">
                  {(m.features || []).map((f) => (
                    <li key={f} className="flex gap-2">
                      <Check
                        className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${
                          m.highlight ? "text-cyan-400" : "text-slate-500"
                        }`}
                      />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button
                  data-testid={`tier-${p.id}-cta`}
                  onClick={() => buy(p.id)}
                  disabled={loading === p.id}
                  className={`mt-5 w-full rounded-sm font-semibold ${
                    m.highlight
                      ? "bg-cyan-500 hover:bg-cyan-400 text-slate-950"
                      : "bg-slate-800 hover:bg-slate-700 text-slate-100"
                  }`}
                >
                  {loading === p.id ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      Opening checkout…
                    </>
                  ) : (
                    `Get ${p.name} · ₹${p.amount}`
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        <p
          data-testid="pricing-risk-reversal"
          className="text-[11px] text-slate-400 text-center pt-3"
        >
          One-time payment · credits never expire · full refund on any unused
          pack
        </p>
        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-600 text-center pt-2">
          Secured by Razorpay · cards, UPI, netbanking, wallets · test mode
        </p>
      </DialogContent>
    </Dialog>
    <AuthDialog
      open={authOpen}
      onOpenChange={setAuthOpen}
      title="Sign up to unlock paid credits"
      onAuthed={() => {
        if (pendingPack) {
          const id = pendingPack;
          setPendingPack(null);
          setTimeout(() => startCheckout(id), 200);
        }
      }}
    />
    </>
  );
}
